import moment from 'moment';
import { Box, IconButton, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';
import UndoIcon from '@mui/icons-material/Undo';
import { useNavigate } from 'react-router-dom';
import { PushPin } from '@mui/icons-material';
import { getFeatureDetailList, getTaxonomyExpandedReport } from '../../../features';
import palette from '../../../theme/palette';
import { fLargeNumber } from '../../../utils/formatNumber';
import { getCompletedDataLabel } from '../../../utils/getDate';
import UsersTrend from '../components/UsersTrend';

function RenderHeaderWithSub({ main, sub }) {
  return (
    <Box display="flex" flexDirection="column" alignItems="flex-start">
      <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
        {main}
      </Typography>
      <Typography variant="caption" color="text.secondary" lineHeight={1} visibility={sub ? 'visible' : 'hidden'}>
        {sub || main}
      </Typography>
    </Box>
  );
}

function LabelCell({ row, selectedRadioId, onClick }) {
  const {
    id, parentId, name,
  } = row;

  // Determine if clickable
  const isClickable = !(id === selectedRadioId && parentId === null);
  const showBackArrow = id === selectedRadioId && !!parentId;

  const handleClick = () => {
    if (isClickable && onClick) onClick(row);
  };

  return (
    <Box display="flex" alignItems="center">
      {showBackArrow && (
        <IconButton
          size="small"
          onClick={handleClick}
          sx={{ mr: 0.5 }}
        >
          <UndoIcon fontSize="small" />
        </IconButton>
      )}
      <Typography
        variant="body2"
        onClick={handleClick}
        sx={{
          cursor: isClickable ? 'pointer' : 'default',
          color: id === selectedRadioId ? 'primary.main' : 'text.primary',
          textDecoration: isClickable ? 'underline' : 'none',
          '&:hover': isClickable ? { color: 'primary.dark' } : {},
        }}
      >
        {name}
      </Typography>
    </Box>
  );
}

export default function useFeatureTaxonomyTable({
  allAccountStats,
  selectedView,
  levelMap,
  viewOption,
  selectedRadioId,
  setExpanded,
  setPreviewData,
  setExpandedReportId,
  handleApiCall,
  setOpenFeatureModal,
}) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const completedLabel = getCompletedDataLabel([], viewOption);
  const formattedLabel = moment(
    completedLabel,
    viewOption === 'month' ? 'MM-YYYY' : 'MM-DD-YYYY',
  ).format(viewOption === 'month' ? 'MMM YYYY' : 'DD MMMM');

  const checkNodeValue = (userCount, value) => {
    if (Object.keys(userCount).length > 0) return value;
    return 'N/A';
  };

  const handleExpand = (row) => {
    const { id, featureId, name } = row;
    const payload = {
      name,
      id: featureId || id,
      type: featureId ? 'feature' : 'node',
      viewOption,
    };
    setExpandedReportId(payload);

    dispatch(getTaxonomyExpandedReport(payload)).then((res) => {
      setPreviewData({
        featureInfo: {
          ...res.payload.featureInfo,
          featureName: '',
          isNestedFeature: false,
          featureId: featureId ?? id,
        },
        timePeriod: res.payload.timePeriod,
        totalActiveAccount: res.payload.totalActiveAccount,
        totalActiveSeats: res.payload.totalActiveSeats,
      });
    });
  };

  const handleFeatureClick = (featureId) => {
    if (featureId) {
      dispatch(getFeatureDetailList(featureId));
      setOpenFeatureModal();
    }
  };

  const handleNodeClick = (row) => {
    const { id, parentId, featureId } = row;
    if (featureId) {
      return handleFeatureClick(featureId);
    }

    if (id === selectedRadioId && parentId === null) return null;
    const localId = id === selectedRadioId ? parentId : id;
    setExpanded((prev) => (prev.includes(localId) ? prev : [...prev, localId]));
    return handleApiCall(localId);
  };

  const handleNavigateToAccounts = (row) => {
    const data = {
      ...(row.featureId
        ? { featureId: row.featureId }
        : { nodeId: row.id }
      ),
      view: viewOption,
    };
    navigate('/account-list', { state: { isNavigatedFromFeatureTaxonomy: true, featureAccountsQueryData: data } });
  };

  const columns = [
    {
      field: 'pin',
      headerName: '',
      width: 20,
      sortable: false,
      isRowSelectable: false,
      renderCell: (params) => (params.row.isCoreFeature ? <PushPin sx={{ color: palette.text.primary, fontSize: 18 }} /> : null),
    },
    {
      field: 'name',
      headerName: 'Label',
      minWidth: 300,
      flex: 0.8,
      sortable: false,
      renderHeader: () => <RenderHeaderWithSub main="Label" />,
      renderCell: (params) => (
        <LabelCell
          row={params.row}
          selectedRadioId={selectedRadioId}
          onClick={handleNodeClick}
        />
      ),
    },
    ...(selectedView !== 0
      ? [
        {
          field: 'type',
          headerName: 'Type',
          minWidth: 100,
          flex: 0.7,
          sortable: false,
          renderHeader: () => <RenderHeaderWithSub main="Type" />,
          renderCell: (params) => (params.row.featureId ? 'Feature' : levelMap[params.row.level] ?? 'N/A'),
        },
      ]
      : []),
    {
      field: 'userCount',
      headerName: 'History',
      minWidth: 180,
      flex: 0.8,
      sortable: false,
      align: 'center',
      headerAlign: 'center',
      headerClassName: 'center-align--header',
      renderHeader: () => (
        <RenderHeaderWithSub
          main="History"
          sub={`12 ${viewOption.charAt(0).toUpperCase() + viewOption.slice(1)}s`}
        />
      ),
      renderCell: (params) => (Object.keys(params.row.userCount || {}).length > 0 ? (
        <UsersTrend
          trendsData={params.row.userCount}
          handleExpand={() => handleExpand(params.row)}
          viewOption={viewOption}
        />
      ) : (
        'N/A'
      )),
    },
    {
      field: 'lastMonthData',
      headerName: 'Users',
      flex: 0.5,
      minWidth: 100,
      sortable: false,
      type: 'number',
      renderHeader: () => <RenderHeaderWithSub main="Users" sub={formattedLabel} />,
      renderCell: (params) => (params.value ? checkNodeValue(params.row.userCount, params.value) : '--'),
    },
    {
      field: 'growth',
      headerName: 'Trend',
      minWidth: 100,
      flex: 0.5,
      sortable: false,
      type: 'number',
      renderHeader: () => (
        <RenderHeaderWithSub
          main="Trend"
          sub={`${viewOption.charAt(0).toUpperCase() + viewOption.slice(1)}ly`}
        />
      ),
      renderCell: ({ value }) => {
        if (typeof value !== 'string') return <span style={{ color: '#888' }}>--</span>;
        const numericValue = parseFloat(value.replace('%', ''));
        let color = '#888';
        let display = value;
        if (numericValue > 0) {
          color = palette.success.dark;
          display = `+${value}`;
        } else if (numericValue < 0) {
          color = palette.error.main;
        }
        return <span style={{ color }}>{display}</span>;
      },
    },
    {
      field: 'operationCount',
      headerName: 'Operations',
      minWidth: 100,
      flex: 0.5,
      sortable: false,
      type: 'number',
      renderHeader: () => <RenderHeaderWithSub main="Operations" sub={formattedLabel} />,
      renderCell: (params) => checkNodeValue(params.row.userCount, fLargeNumber(params.value)),
    },
    {
      field: 'accountCount',
      headerName: 'Accounts',
      minWidth: 100,
      flex: 0.5,
      sortable: false,
      type: 'number',
      renderHeader: () => <RenderHeaderWithSub main="Accounts" />,
      renderCell: (params) => {
        const { value, row: { id, featureId, userCount } } = params;
        const total = allAccountStats.activeAccounts ?? 0;

        if (!userCount || Object.keys(userCount).length === 0) return 'N/A';
        if (total && value != null) {
          const percent = ((value / total) * 100).toFixed(0);
          return (
            <span
              style={{ cursor: 'pointer', color: '#1976d2' }}
              onClick={() => handleNavigateToAccounts({ id, featureId })}
            >
              {value}
              <span style={{ marginLeft: 8, color: '#888', fontSize: 12 }}>
                (
                {percent}
                %)
              </span>
            </span>
          );
        }
        return (
          <span
            style={{ cursor: 'pointer', color: '#1976d2' }}
            onClick={() => handleNavigateToAccounts({ id, featureId })}
          >
            {value}
          </span>
        );
      },
    },
  ];

  return { columns };
}
